import { useAppSelector } from 'app/hooks';
import { selectMap3dWorkspace } from 'map-3d/shared/map-3d-slices';
import { useEffect, useState } from 'react';
import { LineEventType } from './line-tool';
import { PolygonEventType } from './polygon-tool';

/*
 * Observes whether a polygon or line is currently being drawn, i.e. its first
 * vertex is down but it has not been completed or discarded yet.
 *
 * The tools announce the start (first vertex) and the end (feature created),
 * and a discard — Esc, undoing the last remaining vertex, or switching tool
 * mid-drawing — arrives as the dashed preview edge being deleted. Every one of
 * those re-reads the tool itself, so the tool stays the single source of truth
 * and no separate reset is needed when the active tool changes.
 */
export const useDrawingInProgress = (): boolean => {
  // States.
  const [isDrawingInProgress, setIsDrawingInProgress] = useState(false);

  // Selectors.
  const { drawingTools } = useAppSelector(selectMap3dWorkspace);

  // useEffects.
  useEffect(() => {
    const polygonTool = drawingTools?.polygonTool;
    const lineTool = drawingTools?.lineTool;

    if (!polygonTool && !lineTool) {
      return;
    }

    const syncDrawingInProgress = () => {
      setIsDrawingInProgress(
        Boolean(
          polygonTool?.isDrawingInProgress || lineTool?.isDrawingInProgress,
        ),
      );
    };

    const polygonEvents = [
      PolygonEventType.DrawingStarted,
      PolygonEventType.DrawingEnd,
      PolygonEventType.DashedLineDeleted,
    ];
    const lineEvents = [
      LineEventType.DrawingStarted,
      LineEventType.DrawingEnd,
      LineEventType.DashedLineDeleted,
    ];

    polygonEvents.forEach((event) => {
      polygonTool?.addEventListener(event, syncDrawingInProgress);
    });
    lineEvents.forEach((event) => {
      lineTool?.addEventListener(event, syncDrawingInProgress);
    });

    syncDrawingInProgress();

    return () => {
      polygonEvents.forEach((event) => {
        polygonTool?.removeEventListener(event, syncDrawingInProgress);
      });
      lineEvents.forEach((event) => {
        lineTool?.removeEventListener(event, syncDrawingInProgress);
      });
    };
  }, [drawingTools]);

  return isDrawingInProgress;
};
